import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import contratoVentaService, {
  ContratoCertificacion,
  ContratoItem,
} from '../../../services/contratoVentaService';
import { formatCurrency } from '../../../utils/formatters';

interface ContratoCertificacionesTabProps {
  contratoId: number;
  items: ContratoItem[];
  certificaciones: ContratoCertificacion[];
  onChanged: () => void;
  readOnly?: boolean;
}

const ContratoCertificacionesTab: React.FC<ContratoCertificacionesTabProps> = ({
  contratoId,
  items,
  certificaciones,
  onChanged,
  readOnly,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const itemsAvance = useMemo(
    () => items.filter((i) => i.tipoFacturacion === 'PorAvance' && i.id),
    [items]
  );
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    contratoItemId: 0,
    fecha: new Date().toISOString().slice(0, 10),
    porcentajeAvance: 10,
    nroOrdenCompra: '',
    observaciones: '',
  });

  const handleAdd = async () => {
    if (!form.contratoItemId) {
      enqueueSnackbar('Seleccioná un ítem por avance', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      await contratoVentaService.addCertificacion(contratoId, {
        contratoItemId: form.contratoItemId,
        fecha: form.fecha,
        porcentajeAvance: form.porcentajeAvance,
        nroOrdenCompra: form.nroOrdenCompra || undefined,
        observaciones: form.observaciones || undefined,
      });
      enqueueSnackbar('Certificación registrada', { variant: 'success' });
      setOpen(false);
      onChanged();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al registrar certificación', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (certId: number) => {
    if (!window.confirm('¿Eliminar esta certificación?')) return;
    try {
      await contratoVentaService.deleteCertificacion(contratoId, certId);
      enqueueSnackbar('Certificación eliminada', { variant: 'success' });
      onChanged();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'No se pudo eliminar', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Certificá avances sobre ítems configurados como “Por avance”.
        </Typography>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={itemsAvance.length === 0}
            onClick={() => {
              setForm((f) => ({
                ...f,
                contratoItemId: itemsAvance[0]?.id || 0,
              }));
              setOpen(true);
            }}
          >
            Nueva certificación
          </Button>
        )}
      </Stack>

      {itemsAvance.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No hay ítems por avance. Cambiá el tipo de facturación de un producto a “Por avance”.
        </Typography>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fecha</TableCell>
            <TableCell>Producto</TableCell>
            <TableCell align="right">% Avance</TableCell>
            <TableCell align="right">% Acum.</TableCell>
            <TableCell align="right">Importe</TableCell>
            <TableCell align="right">Pendiente</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Factura</TableCell>
            {!readOnly && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {certificaciones.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                Sin certificaciones
              </TableCell>
            </TableRow>
          ) : (
            certificaciones.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{String(c.fecha).slice(0, 10)}</TableCell>
                <TableCell>{c.codigoProducto}</TableCell>
                <TableCell align="right">{c.porcentajeAvance}%</TableCell>
                <TableCell align="right">{c.porcentajeAcumulado}%</TableCell>
                <TableCell align="right">{formatCurrency(c.importe)}</TableCell>
                <TableCell align="right">{formatCurrency(c.importePendiente)}</TableCell>
                <TableCell>
                  <Chip size="small" label={c.estado} />
                </TableCell>
                <TableCell>{c.nroFormularioFactura || '—'}</TableCell>
                {!readOnly && (
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={c.estado === 'Facturado'}
                      onClick={() => handleDelete(c.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva certificación de avance</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Ítem"
              fullWidth
              value={form.contratoItemId}
              onChange={(e) => setForm({ ...form, contratoItemId: Number(e.target.value) })}
            >
              {itemsAvance.map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.codigoProducto} — {i.descripcionPlantilla.slice(0, 40)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label="Fecha"
              InputLabelProps={{ shrink: true }}
              value={form.fecha}
              onChange={(e) => setForm({ ...form, fecha: e.target.value })}
              fullWidth
            />
            <TextField
              type="number"
              label="% Avance"
              value={form.porcentajeAvance}
              onChange={(e) => setForm({ ...form, porcentajeAvance: Number(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0.01, max: 100, step: 0.01 }}
            />
            <TextField
              label="Orden de compra"
              value={form.nroOrdenCompra}
              onChange={(e) => setForm({ ...form, nroOrdenCompra: e.target.value })}
              fullWidth
            />
            <TextField
              label="Observaciones"
              value={form.observaciones}
              onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContratoCertificacionesTab;
